import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'
import { ShieldCheck, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { LogoMark } from '@/components/generals/logo'
import { Sparkline } from '@/components/charts/sparkline'
import { FloatingCard, PreviewFrame } from '@/components/features/preview-frame'
import { CASH_SERIES, CHART_COLORS } from '@/constants/dashboard'

type QuestionKey = 'margin' | 'costs' | 'cash'

const QUESTIONS: Array<{ id: QuestionKey, question: string, answer: string, source: string }> = [
    {
        id: 'margin',
        question: 'Why did gross margin dip in August?',
        answer: 'Gross margin fell from 73.1% to 71.8% in August. Cloud infrastructure rose 14% after usage spikes from two enterprise onboardings. September has already recovered to 72.4%.',
        source: 'Based on 1,284 transactions and 3 vendor invoices'
    },
    {
        id: 'costs',
        question: 'Where can we cut costs this quarter?',
        answer: 'Three opportunities add up to about $7,900 a month, with no impact on headcount or customers.',
        source: 'Based on 214 software subscriptions and usage data'
    },
    {
        id: 'cash',
        question: 'How is our cash position trending?',
        answer: 'Cash grew 7.1% in September to $2.49M. At the current pace you will pass $3M in January, two months ahead of plan.',
        source: 'Based on 12 connected bank and card accounts'
    }
]

const MARGIN = [
    { label: 'Apr', value: 72.2 },
    { label: 'May', value: 72.8 },
    { label: 'Jun', value: 73.0 },
    { label: 'Jul', value: 73.1 },
    { label: 'Aug', value: 71.8 },
    { label: 'Sep', value: 72.4 }
]

const SAVINGS = [
    { label: 'Consolidate project management tools', value: 4200 },
    { label: 'Right-size two idle cloud reservations', value: 2350 },
    { label: 'Remove unused design seats', value: 1350 }
]

const AnswerVisual: FC<{ id: QuestionKey }> = ({ id }): ReactNode => {
    if (id === 'margin') {
        return (
            <div aria-hidden='true' className='mt-4 flex h-24 items-end gap-2'>
                {MARGIN.map((item, index) => (
                    <div key={item.label} className='flex flex-1 flex-col items-center gap-1.5'>
                        <span className='text-[10px] tabular-nums text-stone'>{item.value.toFixed(1)}%</span>

                        <span
                            className='w-full animate-in rounded-t-[4px] fade-in slide-in-from-bottom-2 duration-700'
                            style={{
                                height: `${(item.value - 70) * 22}px`,
                                backgroundColor: item.label === 'Aug' ? CHART_COLORS.terracotta : CHART_COLORS.burgundy,
                                opacity: item.label === 'Aug' ? 1 : 0.8,
                                animationDelay: `${index * 60}ms`
                            } as CSSProperties}
                        />

                        <span className='text-[10px] text-stone'>{item.label}</span>
                    </div>
                ))}
            </div>
        )
    }

    if (id === 'costs') {
        return (
            <ul className='mt-4 space-y-2'>
                {SAVINGS.map((item, index) => (
                    <li
                        key={item.label}
                        className='flex animate-in items-center gap-3 rounded-lg bg-ivory px-3 py-2 text-xs fade-in slide-in-from-bottom-1 duration-500'
                        style={{ animationDelay: `${index * 80}ms` }}
                    >
                        <span className='flex-1 text-stone-dark'>{item.label}</span>
                        <span className='font-medium tabular-nums text-positive'>{formatCurrency(item.value)}/mo</span>
                    </li>
                ))}
            </ul>
        )
    }

    return (
        <div className='mt-4 rounded-lg bg-ivory p-3'>
            <div className='flex items-baseline justify-between text-xs'>
                <span className='text-stone-dark'>Cash balance, last 12 months</span>
                <span className='font-medium tabular-nums'>$2.49M</span>
            </div>

            <Sparkline values={CASH_SERIES} color={CHART_COLORS.burgundy} height={56} animation='autoplay' className='mt-3' />
        </div>
    )
}

export const InsightsPreview: FC = (): ReactNode => {
    const [selected, setSelected] = useState<QuestionKey>('margin')
    const current = QUESTIONS.find(item => item.id === selected) ?? QUESTIONS[0]

    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex items-center gap-2'>
                    <span className='flex size-8 items-center justify-center rounded-xl bg-burgundy text-gold'>
                        <Sparkles aria-hidden='true' className='size-4' />
                    </span>

                    <div>
                        <p className='text-sm font-medium'>Ask Veyra</p>
                        <p className='text-[11px] text-stone'>Answers grounded in your own ledger</p>
                    </div>
                </div>

                <div role='group' aria-label='Example questions' className='mt-4 flex flex-wrap gap-1.5'>
                    {QUESTIONS.map(item => (
                        <button
                            key={item.id}
                            type='button'
                            aria-pressed={selected === item.id}
                            onClick={() => setSelected(item.id)}
                            className={cn(
                                'rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors duration-300',
                                selected === item.id
                                    ? 'border-burgundy bg-burgundy text-cream'
                                    : 'border-burgundy/15 text-stone-dark hover:border-burgundy/35 hover:text-burgundy'
                            )}
                        >
                            {item.question}
                        </button>
                    ))}
                </div>

                <div key={current.id} aria-live='polite' className='mt-5 space-y-3'>
                    <p className='ml-auto w-fit max-w-[85%] animate-in rounded-2xl rounded-br-md bg-burgundy px-3.5 py-2 text-xs text-cream fade-in slide-in-from-right-2 duration-500'>
                        {current.question}
                    </p>

                    <div className='animate-in rounded-2xl rounded-bl-md border border-burgundy/[0.08] bg-cream/60 p-4 fade-in slide-in-from-bottom-2 duration-700'>
                        <p className='flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>
                            <LogoMark className='size-3.5 text-gold-dark' />
                            Veyra AI
                        </p>

                        <p className='mt-2 text-[13px] leading-relaxed text-burgundy'>{current.answer}</p>

                        <AnswerVisual id={current.id} />
                    </div>
                </div>
            </PreviewFrame>

            <FloatingCard slow className='-bottom-7 -left-4 w-64 lg:-left-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <ShieldCheck aria-hidden='true' className='size-4 text-sage-dark' />
                    Confidence: high
                </p>

                <p className='mt-1 text-[11px] text-stone'>{current.source}</p>
            </FloatingCard>
        </div>
    )
}
